import { Component, Input, OnChanges, OnDestroy } from '@angular/core';

import { Subscription } from 'rxjs';

import { CostCenter } from 'src/shared/models/cost-center.model';
import { CostCenterConfigurationService } from 'src/shared/services/cost-center-configuration.service';
import { JobTitle } from 'src/shared/models/job-title.model';
import { MiscUtil } from 'src/shared/utilities/misc.util';
import { ShiftScheduleService } from 'src/shared/services/shift-schedule.service';
import { ShiftSchedule, ShiftBlockType } from 'src/shared/models/shift-schedule.model';
import { StaffingGridConfiguration, StaffingGridConstants, StaffingGridType } from '../utilities/staffing-grid-constants.util';
import { StaffingGridUtil } from '../utilities/staffing-grid.util';
import { StartHour } from '../models/start-hour.model';
import { WorkflowPageUtil } from 'src/app/workflow/base-classes/workflow-page/workflow-page.util';

@Component({
    selector: 'smt-staffing-grid-buttons',
    templateUrl: './buttons.component.html',
    styleUrls: ['./buttons.component.scss']
})
export class ButtonsComponent implements OnChanges, OnDestroy {
    @Input() costCenter: CostCenter;
    @Input() readOnly: boolean;

    confirmDelete: Function;
    gridTypes: StaffingGridType[];
    selectedGridConfiguration: StaffingGridConfiguration;
    selectedGridType: StaffingGridType;
    shiftSchedules: ShiftSchedule[];
    showDeleteWarning = false;

    readonly acuityGrid = StaffingGridConfiguration.Acuity;
    readonly inpatientGrid = StaffingGridConfiguration.Inpatient;
    readonly totalUosGrid = StaffingGridConfiguration.TotalUos;

    private buttonsSubscription: Subscription;

    constructor(
        private _costCenterConfigurationService: CostCenterConfigurationService, 
        private _shiftScheduleService: ShiftScheduleService) { 
        this.buttonsSubscription = this._shiftScheduleService.schedules$.subscribe((schedules) => {
            this.shiftSchedules = schedules;
        });
    }

    ngOnChanges() {
        this.gridTypes = StaffingGridUtil.getGridTypesFromCostCenter(this.costCenter);
        
        if (WorkflowPageUtil.hasAcuityConfiguration(this.costCenter) ||
            WorkflowPageUtil.hasAcuityWithRoomsConfiguration(this.costCenter)) {
            this.selectedGridConfiguration = this.acuityGrid;
        }
        else if (WorkflowPageUtil.hasInpatientConfiguration(this.costCenter)) {
            this.selectedGridConfiguration = this.inpatientGrid;
        }
        else if (WorkflowPageUtil.hasUosConfiguration(this.costCenter)) {
            this.selectedGridConfiguration = this.totalUosGrid;
        }
    }  

    ngOnDestroy() {
        if (this.buttonsSubscription) {
            this.buttonsSubscription.unsubscribe();
        }
    }
    
    get activeShiftSchedules() : ShiftSchedule[] {
        if (!this.shiftSchedules) {
            return [];
        }
    
        return this.shiftSchedules.filter(s => !s.inactiveDate);
    }       

    enableFixedBySchedule(schedule: ShiftSchedule): boolean {
        if (!schedule) {     
            return false;
        }

        if (schedule.isSaturday && this.enableSaturdayFixed()) {
            return true;
        }

        if (schedule.isSunday && this.enableSundayFixed()) {
            return true;
        }

        if (schedule.isWeekday && this.enableWeekdayFixed()) {
            return true;
        }

        if (schedule.isWeekend && this.enableWeekendFixed()) {
            return true;
        }

        return false;
    }

    enableVariableBySchedule(schedule: ShiftSchedule): boolean {
        if (!schedule) {     
            return false;
        }

        if (schedule.isSaturday && this.enableSaturdayVariable()) {
            return true;
        }

        if (schedule.isSunday && this.enableSundayVariable()) {
            return true;
        }

        if (schedule.isWeekday && this.enableWeekdayVariable()) {
            return true;
        }

        if (schedule.isWeekend && this.enableWeekendVariable()) {
            return true;
        }

        return false;
    }
    
    enableSaturdayFixed() {
        return this.activeShiftSchedules.some(s => s.isSaturday && s.startHour != null) && this.hasFixedJobTitles();
    }

    enableSaturdayVariable() {
        return this.activeShiftSchedules.some(s => s.isSaturday && s.startHour != null) && this.hasVariableJobTitles();
    }

    enableSundayFixed() {
        return this.activeShiftSchedules.some(s => s.isSunday && s.startHour != null) && this.hasFixedJobTitles();
    }

    enableSundayVariable() {
        return this.activeShiftSchedules.some(s => s.isSunday && s.startHour != null) && this.hasVariableJobTitles();
    }

    enableWeekdayFixed() {
        return this.activeShiftSchedules.some(s => s.isWeekday && s.startHour != null) && this.hasFixedJobTitles();
    }

    enableWeekdayVariable() {
        return this.activeShiftSchedules.some(s => s.isWeekday && s.startHour != null) && this.hasVariableJobTitles();
    }

    enableWeekendFixed() {
        return this.activeShiftSchedules.some(s => s.isWeekend && s.startHour != null) && this.hasFixedJobTitles();
    }

    enableWeekendVariable() {
        return this.activeShiftSchedules.some(s => s.isWeekend && s.startHour != null) && this.hasVariableJobTitles();
    }

    formatShiftBlockType(typeId: ShiftBlockType) {        
        let type = StaffingGridConstants.ShiftBlockOptions.find(type => type.id == typeId);
        
        if (type) {
            return type.description;
        }
        
        return "None";
    }

    formatShiftBlockHours(schedule: ShiftSchedule){
        if(schedule.isClosed)
          return "-";
        
        let startHourString: string;
        let endHourString: string;
    
        let blockString = schedule.blockLength + " hour blocks ";
        let startHourBlock = this.getShiftBlockHour(schedule.startHour);
        if(startHourBlock)
          startHourString = startHourBlock.description;
    
        //when 'No End time'
        if(schedule.endHour == null)
          endHourString = startHourString;
        else{
          let endtHourBlock = this.getShiftBlockHour(schedule.endHour);
          if(endtHourBlock)
            endHourString = endtHourBlock.description; 
        }                   
    
        return blockString + startHourString + " - " + endHourString;
    }
    
    getShiftBlockHour(hour: number): StartHour {
        return StaffingGridConstants.shiftBlockHours.find(blockHour => blockHour.hour == hour);
    }
    
    hasFixedJobTitles(): boolean {
        return this.costCenter && MiscUtil.checkArray(this.costCenter.fixedJobTitles);
    }

    hasVariableJobTitles(): boolean {
        return this.costCenter && MiscUtil.checkArray(this.costCenter.variableJobTitles);
    }

    inactivateShiftSchedule(type: ShiftBlockType) {
        this.showDeleteWarning = true;
    
        this.confirmDelete = async () => {    
            this.setNoneGridType();
                    
            let shiftScheduleIndex = this.shiftSchedules.findIndex(s => s.shiftBlockType === type && s.inactiveDate == null);
            let shiftSchedule = this.shiftSchedules[shiftScheduleIndex];
      
            if (shiftSchedule.id) {
                shiftSchedule.inactiveDate = new Date();
                await this._shiftScheduleService.setShiftSchedules([ shiftSchedule ]);
            } 
            else {
                this.shiftSchedules.splice(shiftScheduleIndex, 1);
            }
                      
            this.showDeleteWarning = false;
        }
    
        return false;
      }

    isAcuityStaffingGridComplete(schedule: ShiftSchedule): boolean {        
        return StaffingGridUtil.checkCompleteAcuityGrid(this.costCenter, schedule);
    }

    isAddOnsStaffingGridComplete(schedule: ShiftSchedule): boolean {        
        return StaffingGridUtil.checkCompleteAddOnGrid(schedule.partialShiftBlocks);
    }

    private isGridTypesValid(): boolean {
        return MiscUtil.checkArray(this.gridTypes);
    }

    private isJobShiftGridCompleteBySchedule(schedule: ShiftSchedule, isFixed: boolean): boolean {
        if (!this.enableFixedBySchedule(schedule) && !this.enableVariableBySchedule(schedule)) {
            return false;
        }

        let jobTitles: JobTitle[];
        let shiftBlocks = StaffingGridUtil.setPartialShiftBlocks(schedule);

        if (isFixed) {
            jobTitles = this.costCenter.fixedJobTitles;            
        }
        else {
            jobTitles = this.costCenter.variableJobTitles;
        }

        let maxCensus = 0;
        let minCensus = 0;

        if (WorkflowPageUtil.hasInpatientConfiguration(this.costCenter)) {
            maxCensus = this.costCenter.inpatientConfiguration.maxCensus;
            minCensus = this.costCenter.inpatientConfiguration.minCensus;
        }

        return StaffingGridUtil.isJobShiftGridComplete(
            jobTitles,
            shiftBlocks,
            isFixed,
            maxCensus,
            minCensus
        );
        
    }

    isSaturdayFixedSelected(): boolean {
        return this.selectedGridType === StaffingGridType.SaturdayFixed;
    }

    isSaturdayVariableSelected(): boolean {
        return this.selectedGridType === StaffingGridType.SaturdayVariable;
    }

    isStaffingGridCompleteBySchedule(schedule: ShiftSchedule, isFixed: boolean): boolean {
        if (isFixed || this.selectedGridConfiguration === StaffingGridConfiguration.Inpatient) {
            return this.isJobShiftGridCompleteBySchedule(schedule, isFixed);
        }
        else if (this.selectedGridConfiguration === StaffingGridConfiguration.Acuity) {
            return this.isAcuityStaffingGridComplete(schedule);
        }
        else if (this.selectedGridConfiguration === StaffingGridConfiguration.TotalUos) {
            return this.isTotalUosVariableStaffingGridComplete(schedule);
        }
             
        return false;
    } 

    isSundayFixedSelected(): boolean {
        return this.selectedGridType === StaffingGridType.SundayFixed;
    }

    isSundayVariableSelected(): boolean {
        return this.selectedGridType === StaffingGridType.SundayVariable;
    }

    isTotalUosVariableStaffingGridComplete(schedule: ShiftSchedule): boolean {
        if (!this.enableVariableBySchedule(schedule) || !MiscUtil.checkArray(this.costCenter.variableJobTitles)) {
            return false;
        }

        StaffingGridUtil.checkTotalUosSchedules(this.costCenter.variableJobTitles, [ schedule ]);

        return schedule.isValid;
    }

    isWeekdayFixedSelected(): boolean {
        return this.selectedGridType === StaffingGridType.WeekdayFixed;
    }       

    isWeekdayFixedStaffingGridComplete(): boolean {   
        if (!MiscUtil.checkArray(this.activeShiftSchedules)) {
            return false;
        }

        let fixedSchedule = this.activeShiftSchedules.find(s => s.isWeekday && this.enableFixedBySchedule(s));

        if (!fixedSchedule) {
            return false;
        }

        return this.isStaffingGridCompleteBySchedule(fixedSchedule, true);
    }        

    isWeekdayVariableSelected(): boolean {
        return this.selectedGridType === StaffingGridType.WeekdayVariable;
    }

    isWeekdayVariableStaffingGridComplete(): boolean {
        if (!MiscUtil.checkArray(this.activeShiftSchedules)) {
            return false;
        }

        let variableSchedule = this.activeShiftSchedules.find(s => s.isWeekday && this.enableVariableBySchedule(s));

        if (!variableSchedule) {
            return false;
        }

        return this.isStaffingGridCompleteBySchedule(variableSchedule, false);      
    }

    isWeekendFixedSelected(): boolean {
        return this.selectedGridType === StaffingGridType.WeekendFixed;
    }

    isWeekendFixedStaffingGridComplete(): boolean {
        if (!MiscUtil.checkArray(this.activeShiftSchedules)) {
            return false;
        }

        let fixedSchedule = this.activeShiftSchedules.find(s => s.isWeekend && this.enableFixedBySchedule(s));

        if (!fixedSchedule) {
            return false;
        }

        return this.isStaffingGridCompleteBySchedule(fixedSchedule, true);
    }

    isWeekendVariableSelected(): boolean {
        return this.selectedGridType === StaffingGridType.WeekendVariable;
    }   

    isWeekendVariableStaffingGridComplete(): boolean {
        if (!MiscUtil.checkArray(this.activeShiftSchedules)) {
            return false;
        }

        let variableSchedule = this.activeShiftSchedules.find(s => s.isWeekend && this.enableVariableBySchedule(s));

        if (!variableSchedule) {
            return false;
        }

        return this.isStaffingGridCompleteBySchedule(variableSchedule, false);       
    }

    setGridTypeBySchedule(schedule: ShiftSchedule, isFixed: boolean, isAddOn: boolean) {
        if (!schedule) {
            return;
        }    
        
        if (schedule.isWeekday && schedule.isWeekend) {
            if (isAddOn) {
                this.setAllWeekAddOnGridType();
            }
            else if (isFixed) {
                this.setAllWeekFixedGridType();
            }
            else {
                this.setAllWeekVariableGridType();
            }
        }

        if (schedule.isSaturday && isAddOn) {
            this.setSaturdayAddOnGridType();
        }

        if (schedule.isSaturday && isFixed && !isAddOn) {
            this.setSaturdayFixedGridType();
        }

        if (schedule.isSaturday && !isFixed && !isAddOn) {
            this.setSaturdayVariableGridType();
        }

        if (schedule.isSunday && isAddOn) {
            this.setSundayAddOnGridType();
        }

        if (schedule.isSunday && isFixed && !isAddOn) {
            this.setSundayFixedGridType();
        }

        if (schedule.isSunday && !isFixed && !isAddOn) {
            this.setSundayVariableGridType();
        }

        if (schedule.isWeekday && !schedule.isWeekend && isAddOn) {
            this.setWeekdayAddOnGridType();
        }

        if (schedule.isWeekday && !schedule.isWeekend && isFixed && !isAddOn) {
            this.setWeekdayFixedGridType();
        }

        if (schedule.isWeekday && !schedule.isWeekend && !isFixed && !isAddOn) {
            this.setWeekdayVariableGridType();
        }

        if (schedule.isWeekend && !schedule.isWeekday && isAddOn) {
            this.setWeekendAddOnGridType();
        }

        if (schedule.isWeekend && !schedule.isWeekday && isFixed && !isAddOn) {
            this.setWeekendFixedGridType();
        }

        if (schedule.isWeekend && !schedule.isWeekday && !isFixed && !isAddOn) {
            this.setWeekendVariableGridType();
        }
    }

    private setSelectedGridType(chosenGridType: StaffingGridType) {
        this.selectedGridType = chosenGridType;
    }

    setAllWeekAddOnGridType() {
        this.setSelectedGridType(StaffingGridType.AllWeekAddOns);
        this._costCenterConfigurationService.announceGridTypeButtonPressed(StaffingGridType.AllWeekAddOns);
    }

    setAllWeekFixedGridType() {
        this.setSelectedGridType(StaffingGridType.AllWeekFixed);
        this._costCenterConfigurationService.announceGridTypeButtonPressed(StaffingGridType.AllWeekFixed);
    }

    setAllWeekVariableGridType() {
        this.setSelectedGridType(StaffingGridType.AllWeekVariable);
        this._costCenterConfigurationService.announceGridTypeButtonPressed(StaffingGridType.AllWeekVariable);
    }

    setNoneGridType() {
        this.setSelectedGridType(StaffingGridType.None);
        this._costCenterConfigurationService.announceGridTypeButtonPressed(StaffingGridType.None);
    }

    setSaturdayAddOnGridType() {
        this.setSelectedGridType(StaffingGridType.SaturdayAddOns);
        this._costCenterConfigurationService.announceGridTypeButtonPressed(StaffingGridType.SaturdayAddOns);
    }

    setSaturdayFixedGridType() {
        this.setSelectedGridType(StaffingGridType.SaturdayFixed);
        this._costCenterConfigurationService.announceGridTypeButtonPressed(StaffingGridType.SaturdayFixed);
    }

    setSaturdayVariableGridType() {
        this.setSelectedGridType(StaffingGridType.SaturdayVariable);
        this._costCenterConfigurationService.announceGridTypeButtonPressed(StaffingGridType.SaturdayVariable);
    }

    setSundayAddOnGridType() {
        this.setSelectedGridType(StaffingGridType.SundayAddOns);
        this._costCenterConfigurationService.announceGridTypeButtonPressed(StaffingGridType.SundayAddOns);
    }

    setSundayFixedGridType() {
        this.setSelectedGridType(StaffingGridType.SundayFixed);
        this._costCenterConfigurationService.announceGridTypeButtonPressed(StaffingGridType.SundayFixed);
    }

    setSundayVariableGridType() {
        this.setSelectedGridType(StaffingGridType.SundayVariable);
        this._costCenterConfigurationService.announceGridTypeButtonPressed(StaffingGridType.SundayVariable);
    }

    setWeekdayAddOnGridType() {
        this.setSelectedGridType(StaffingGridType.WeekdayAddOns);
        this._costCenterConfigurationService.announceGridTypeButtonPressed(StaffingGridType.WeekdayAddOns);
    }

    setWeekdayFixedGridType() {
        this.setSelectedGridType(StaffingGridType.WeekdayFixed);
        this._costCenterConfigurationService.announceGridTypeButtonPressed(StaffingGridType.WeekdayFixed);
    }

    setWeekdayVariableGridType() {
        this.setSelectedGridType(StaffingGridType.WeekdayVariable);
        this._costCenterConfigurationService.announceGridTypeButtonPressed(StaffingGridType.WeekdayVariable);
    }

    setWeekendAddOnGridType() {
        this.setSelectedGridType(StaffingGridType.WeekendAddOns);
        this._costCenterConfigurationService.announceGridTypeButtonPressed(StaffingGridType.WeekendAddOns);
    }

    setWeekendFixedGridType() {
        this.setSelectedGridType(StaffingGridType.WeekendFixed);
        this._costCenterConfigurationService.announceGridTypeButtonPressed(StaffingGridType.WeekendFixed);
    }

    setWeekendVariableGridType() {
        this.setSelectedGridType(StaffingGridType.WeekendVariable);
        this._costCenterConfigurationService.announceGridTypeButtonPressed(StaffingGridType.WeekendVariable);
    }

    showAddOns(): boolean {
        return WorkflowPageUtil.hasUosConfiguration(this.costCenter) && this.costCenter.ancillaryConfiguration.addOns;
    }    

    private showByGridType(gridType: StaffingGridType): boolean {
        return this.isGridTypesValid() && this.gridTypes.some(s => s === gridType);
    }  

    showSaturdayFixed(): boolean {
        return this.showByGridType(StaffingGridType.SaturdayFixed);
    }

    showSaturdayVariable(): boolean {
        return this.showByGridType(StaffingGridType.SaturdayVariable);
    }

    showSundayFixed(): boolean {
        return this.showByGridType(StaffingGridType.SundayFixed);
    }

    showSundayVariable(): boolean {
        return this.showByGridType(StaffingGridType.SundayVariable);
    }

    showWeekdayFixed(): boolean {
        return this.showByGridType(StaffingGridType.WeekdayFixed);
    }

    showWeekdayVariable(): boolean {
        return this.showByGridType(StaffingGridType.WeekdayVariable);
    }

    showWeekendFixed(): boolean {
        return this.showByGridType(StaffingGridType.WeekendFixed);
    }

    showWeekendVariable(): boolean {
        return this.showByGridType(StaffingGridType.WeekendVariable);
    } 
}
